import { useEffect, useMemo, useState } from 'react';
import { Dispatch, SetStateAction } from 'react';

import { useFastshiftSelector } from '@/state/hook';

import { JiraBoard, JiraBoardEmbedState, CustomerDomainJiraBoardEmbedType } from './types';

const getEmbedUrlByType = (
	board: JiraBoard | null,
	type: CustomerDomainJiraBoardEmbedType,
): string | null => {
	return board?.embeds.find((embed) => embed.type === type)?.url ?? null;
};

interface useJiraBoardEmbedState {
	boards: JiraBoardEmbedState[];
	selectedBoard: JiraBoardEmbedState | null;
	setSelectedBoard: Dispatch<SetStateAction<JiraBoardEmbedState | null>>;
	selectedEmbedViewUrl: string | null;
}

export const useJiraBoardEmbeds = (): useJiraBoardEmbedState => {
	const { data: engagementData } = useFastshiftSelector((state) => state.customerDomainEngagement);

	const [selectedBoard, setSelectedBoard] = useState<JiraBoardEmbedState | null>(null);

	const boards: JiraBoardEmbedState[] = useMemo(() => {
		return engagementData
			? engagementData.engagementProjects
					.flatMap((project) =>
						project.boards.map((board) => {
							const timelineUrl = getEmbedUrlByType(
								board,
								CustomerDomainJiraBoardEmbedType.TIMELINE,
							);
							const calendarUrl = getEmbedUrlByType(
								board,
								CustomerDomainJiraBoardEmbedType.CALENDAR,
							);

							if (!timelineUrl && !calendarUrl) return null;

							return {
								timelineUrl,
								calendarUrl,
								selectedView: timelineUrl
									? CustomerDomainJiraBoardEmbedType.TIMELINE
									: CustomerDomainJiraBoardEmbedType.CALENDAR,
								board,
								projectName: project.projectName,
								projectIcon: project.projectIcon,
								projectKey: project.projectKey,
							};
						}),
					)
					.filter((boardState) => boardState !== null)
			: [];
	}, [engagementData]);

	useEffect(() => {
		if (boards.length > 0 && boards[0]) {
			setSelectedBoard(boards[0]);
		}
	}, [boards]);

	const selectedEmbedViewUrl = useMemo(() => {
		if (!selectedBoard) {
			return null;
		}

		switch (selectedBoard.selectedView) {
			case CustomerDomainJiraBoardEmbedType.CALENDAR:
				return selectedBoard.calendarUrl;
			case CustomerDomainJiraBoardEmbedType.TIMELINE:
				return selectedBoard.timelineUrl;
			default:
				return null;
		}
	}, [selectedBoard]);

	return { boards, selectedBoard, setSelectedBoard, selectedEmbedViewUrl };
};
